import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, ConfigProvider, Slider, Switch } from 'antd';
import { useTheme } from 'styled-components';
import { Icon } from 'Components/UI/Icon/Icon';
import { IconName } from 'Components/UI/Icon/IconClasses';
import { BimSectionPlane } from 'Data/interfaces/BimViewer/IBimViewerPort';
import {
  SectionPanelWrapper,
  SectionHeader,
  SectionRow,
  AxisGroup,
  AxisButton,
} from './styles';

interface IBimSectionPanelProps {
  section: BimSectionPlane;
  onChange(next: Partial<BimSectionPlane>): void;
}

// Os eixos do motor são nomeados pela direção do olhar do corte, não por X/Y/Z:
// `down` corta na horizontal (planta), `front` e `side` na vertical.
const AXES: { axis: BimSectionPlane['axis']; icon: IconName; labelKey: string }[] = [
  { axis: 'side', icon: 'corteLateral', labelKey: 'bim3dSection.axisSide' },
  { axis: 'front', icon: 'corteFrontal', labelKey: 'bim3dSection.axisFront' },
  { axis: 'down', icon: 'corteHorizontal', labelKey: 'bim3dSection.axisHorizontal' },
];

/**
 * Criar/editar/remover um plano de corte. Cada mudança vai direto pro motor, que
 * aplica no frame seguinte — arrastar o slider corta em tempo real, sem
 * recarregar nem re-tesselar o modelo.
 */
export const BimSectionPanel: FC<IBimSectionPanelProps> = ({ section, onChange }) => {
  const { t } = useTranslation('viewer');
  const theme = useTheme();

  return (
    <ConfigProvider
      theme={{
        token: { colorPrimary: theme.colors.primary.primary },
        // O padrão do antd pinta barra e bolinha num tom claro da primária.
        components: {
          Slider: {
            trackBg: theme.colors.primary.primary,
            trackHoverBg: theme.colors.primary.primary,
            handleColor: theme.colors.primary.primary,
            handleActiveColor: theme.colors.primary.primary,
          },
        },
      }}
    >
      <SectionPanelWrapper>
        <SectionHeader>
          <span className="sectionTitle">{t('bim3dSection.title')}</span>
          <Switch
            size="small"
            checked={section.enabled}
            onChange={(enabled) => onChange({ enabled })}
          />
        </SectionHeader>

        <AxisGroup>
          {AXES.map(({ axis, icon, labelKey }) => (
            <AxisButton
              key={axis}
              type="button"
              $active={section.axis === axis}
              // Trocar de eixo com o corte desligado não mostraria nada; ligar
              // junto é o que o usuário quer em 100% dos casos.
              onClick={() => onChange({ axis, enabled: true })}
            >
              <Icon icon={icon} customSize={16} customColor={theme.colors.surface.onSurfaceVariant} />
              {t(labelKey)}
            </AxisButton>
          ))}
        </AxisGroup>

        <SectionRow>
          <span className="sectionLabel">{t('bim3dSection.position')}</span>
          <Slider
            className="sectionSlider"
            min={0}
            max={100}
            value={section.position}
            disabled={!section.enabled}
            tooltip={{ open: false }}
            onChange={(position: number) => onChange({ position })}
          />
          <span className="sectionValue">{section.position}%</span>
        </SectionRow>

        <SectionRow>
          <span className="sectionLabel">{t('bim3dSection.flip')}</span>
          <Checkbox
            checked={!!section.flipped}
            disabled={!section.enabled}
            onChange={(e) => onChange({ flipped: e.target.checked })}
          />
        </SectionRow>
      </SectionPanelWrapper>
    </ConfigProvider>
  );
};
